import type { DailyBucket } from '../../types/dashboard'
import { Bar } from 'react-chartjs-2'

import type { WidgetSettings } from '../../stores/monthlyDashboardLayoutStore'
import { useChartColors } from '../../hooks/useChartColors'
import { useThemeStore } from '../../stores/themeStore'

export default function DailyChart({ daily, settings, date, channel = 'all' }: { daily: DailyBucket[], settings?: WidgetSettings, date?: string, channel?: string }) {
    let activeDaily = daily
    // Override 'lastMonth' to 'lastYear' because comparing daily sales across months is not standard
    const configuredComp = settings?.chartCompareTo || 'lastYear'
    const comp = configuredComp === 'lastMonth' ? 'lastYear' : configuredComp
    const colors = useChartColors()
    const colorMode = useThemeStore(s => s.getEffectiveColorMode())
    const textColor = colorMode === 'dark' ? '#94a3b8' : '#475569'
    const gridColor = colorMode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'

    if (date) {
        const [yearStr, monthStr] = date.split('-')
        const year = parseInt(yearStr, 10)
        const month = parseInt(monthStr, 10)

        const daysInThisMonth = new Date(year, month, 0).getDate()
        let maxDays = daysInThisMonth

        if (comp === 'lastYear') {
            const daysInLastYear = new Date(year - 1, month, 0).getDate()
            maxDays = Math.max(daysInThisMonth, daysInLastYear)
        }

        activeDaily = daily.slice(0, maxDays)
    }

    const labels = activeDaily.map(d => d.day.toString())
    const showDiscounts = settings?.showDiscounts !== false

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const datasets: any[] = [
        {
            label: 'Net Sales',
            data: activeDaily.map(d => d.sales),
            backgroundColor: colors.primary,
            borderRadius: 4, stack: 'today', order: 2,
        }
    ]

    if (showDiscounts) {
        datasets.push({
            label: 'Discounts',
            data: activeDaily.map(d => d.discount),
            backgroundColor: colors.primarySubtle,
            borderRadius: 4, stack: 'today', order: 2,
        })
    }

    if (comp !== 'none') {
        let compData
        let compLabel
        if (comp === 'lastYear') {
            compData = activeDaily.map(d => d.lastYearSales)
            compLabel = 'Last Year'
        } else {
            compData = activeDaily.map(d => d.lastMonthSales)
            compLabel = 'Last Month'
        }

        datasets.push({
            type: 'line' as const,
            label: compLabel,
            data: compData,
            borderColor: comp === 'lastYear' ? colors.comp2 : colors.comp1,
            borderDash: comp === 'lastYear' ? [2, 2] : [4, 4], borderWidth: 2,
            pointRadius: 0, pointHoverRadius: 4,
            fill: false, order: 1,
        })
    }

    const data = { labels, datasets }

    const options = {
        responsive: true, maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { position: 'bottom', labels: { usePointStyle: true, pointStyle: 'rect', boxWidth: 10, color: textColor, font: { size: 12 } } },
            tooltip: {
                backgroundColor: 'rgba(15, 23, 42, 0.95)', titleFont: { size: 12 }, bodyFont: { size: 11 },
                callbacks: { label: (ctx: any) => `${ctx.dataset.label}: $${(ctx.parsed.y ?? 0).toLocaleString()}` }
            }
        },
        scales: {
            x: { grid: { display: false }, ticks: { color: textColor, font: { size: 11 } } },
            y: {
                stacked: true, grid: { color: gridColor },
                ticks: { color: textColor, font: { size: 11 }, callback: (v: any) => `$${Number(v).toLocaleString()}` }
            }
        }
    }

    return (
        <div className="chart-panel">
            <div className="chart-panel-title">Sales by Day{channel === 'instore' ? ' - In-Store' : channel === 'online' ? ' - Online' : ' - All Channels'}</div>
            <div className="chart-panel-content">
                <Bar options={options as any} data={data as any} />
            </div>
        </div>
    )
}
